import updateModelMember from 'braindamage/api/updateModelMember'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import DodoneAbstractShow from 'app/dodones/AbstractShow'
import DodoneContext from 'app/dodones/Context'
import LoadingScreen from 'app/components/LoadingScreen'
import DodoneNotification from 'app/dodones/Notification'
import useDodoneQuery from 'generated/api/dodones/useDodoneQuery'
import Dodone from 'models/dodone'

export default ({ dodoneId }) => {
  const { isPending, data: { dodone } = {} } = useDodoneQuery({ dodoneId })
  const [isSkipRefetch, setSkipRefetch] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const queryClient = useQueryClient()

  useEffect(() => {
    if (!dodone) return

    DodoneNotification.show(dodone.dodoable.name)
  }, [dodone])

  const onTick = useCallback(
    ({ formatted }) => {
      DodoneNotification.show(formatted)
    },
    [dodone]
  )

  const invalidateQueries = useCallback((dodoneId, refetch = []) => {
    if (dodoneId)
      queryClient.invalidateQueries({ queryKey: ['dodone', dodoneId] })

    queryClient.invalidateQueries({ queryKey: ['dodone', 'current'] })
    queryClient.invalidateQueries({ queryKey: ['dodone', 'today'] })

    refetch.forEach((refetchKey) => {
      queryClient.refetchQueries(['dodone', refetchKey])
    })
  })

  const updateDodone = useMutation({
    mutationFn: async ({ attributes, skipRefetch = false }) => {
      setSkipRefetch(skipRefetch)
      return updateModelMember(`/api/dodones/${dodone.id}`, Dodone, {
        ['dodoneAttributes']: attributes
      })
    },
    onSuccess: async (data) => {
      invalidateQueries(dodone.id)
      queryClient.setQueryData(['dodone', dodone.id], data)
      if (!isSkipRefetch) queryClient.refetchQueries(['dodone', dodone.id])
    },
    onSettled: async (data) => {
      // console.log("onSettled", data);
    },
    onError: async (data) => {
      console.log('onError', data)
    }
  })

  const startDodone = useCallback(() => {
    updateDodone.mutate({ attributes: { started_at: new Date() } })
  }, [])

  const finishDodone = useCallback((params) => {
    params.attributes ||= {}
    params.attributes.finished_at = new Date()
    updateDodone.mutate(params)
  }, [])

  const ignoreDodone = useCallback((params) => {
    updateDodone.mutate({ attributes: { status: 'ignored' } })
  }, [])

  const pendifyDodone = useCallback((params) => {
    updateDodone.mutate({ attributes: { status: 'pending' } })
  }, [])

  const onCableDodoneUpdated = useCallback((data) => {
    const dodoneId = data.detail.dodone.id
    invalidateQueries(dodoneId, [dodoneId, 'today'])
  })

  useEffect(() => {
    document.addEventListener('Dodone#updated', onCableDodoneUpdated)

    return () => {
      document.removeEventListener('Dodone#updated', onCableDodoneUpdated)
    }
  }, [])

  useEffect(() => {
    if (searchParams.get('startPending') == 'true' && dodone?.isPending)
      startDodone()
  }, [dodone])

  if (isPending) return <LoadingScreen />
  if (!isPending && !dodone) return <Navigate to={`/`} replace />

  const dodoneContextValue = {
    dodone: dodone,
    updateDodone: updateDodone,
    startDodone: startDodone,
    finishDodone: finishDodone,
    ignoreDodone: ignoreDodone,
    pendifyDodone: pendifyDodone,
    notification: DodoneNotification,
    onTick: onTick,
    invalidateQueries: invalidateQueries
  }

  return (
    <div className="p-2 m-2 fcol flex-1">
      <DodoneContext.Provider value={dodoneContextValue}>
        <DodoneAbstractShow />
      </DodoneContext.Provider>
    </div>
  )
}
